import React, { useCallback, useState } from "react";
import { BuildingDTO } from "shared/types/building";
import { DocumentFormData } from "../documentUploadModal";
import ButtonNew from "components/buttonNew/buttonNew";
import { Select, SelectItem, Input, Button, Textarea } from "@heroui/react";
import { formatCurrency } from "shared/utils/currency";
import { Info, Trash } from "@phosphor-icons/react";

interface Props {
    formData: DocumentFormData;
    setFormData: (formData: any) => void;
    building: BuildingDTO;
}

export const DocumentLinesSection: React.FC<Props> = ({
    formData,
    setFormData,
    building,
}) => {
    // State for distribution errors (percentage validation)
    const [distributionErrors, setDistributionErrors] = useState<{ [key: number]: string }>({});
    
    // State to track expanded kostenplaats sections
    const [expandedDistributions, setExpandedDistributions] = useState<number[]>([]);
    
    // Toggle kostenplaats expansion
    const toggleDistributionExpansion = (index: number) => {
        setExpandedDistributions(prev => 
            prev.includes(index) 
                ? prev.filter(i => i !== index) 
                : [...prev, index]
        );
    };

    // Add memoization for the totals calculation
    const calculateTotals = useCallback((lines: any[]) => {
        return lines.reduce((acc, line) => ({
            totalExclVat: acc.totalExclVat + Number(line.totalExclVat || 0),
            vatAmount: acc.vatAmount + Number(line.vatAmount || 0),
            totalInclVat: acc.totalInclVat + Number(line.totalInclVat || 0)
        }), { totalExclVat: 0, vatAmount: 0, totalInclVat: 0 });
    }, []);

    // Update the handleLineChange function to properly calculate all values
    const handleLineChange = useCallback((index: number, field: string, value: any) => {
        setFormData((prev: any) => {
            const newLines = [...prev.lines];
            const line = { ...newLines[index] };
            line[field] = value ?? 0;

            // Recalculate totals for the line
            if (field === 'totalExclVat' || field === 'vatPercentage') {
                const totalExclVat = field === 'totalExclVat' ? value : line.totalExclVat;
                const vatPercentage = field === 'vatPercentage' ? value : line.vatPercentage;

                line.vatAmount = totalExclVat * (vatPercentage / 100);
                line.totalInclVat = totalExclVat + line.vatAmount;
            }

            newLines[index] = line;

            // Calculate new totals
            const totals = calculateTotals(newLines);

            return {
                ...prev,
                lines: newLines,
                totalExclVat: totals.totalExclVat,
                vatAmount: totals.vatAmount,
                totalInclVat: totals.totalInclVat
            };
        });
    }, [calculateTotals, setFormData]);

    // Update the handleAnalyticDistributionChange function
    const handleAnalyticDistributionChange = (lineIndex: number, unitId: string, newPercentage: number) => {
        setFormData((prev: DocumentFormData) => {
            const newLines = [...prev.lines];
            const line = { ...newLines[lineIndex] };

            // Update the percentage for the specified unit only
            line.analyticDistributions = line.analyticDistributions.map(dist =>
                dist.unit.id === unitId ? { ...dist, percentage: newPercentage } : dist
            );

            // Calculate total percentage
            const totalPercentage = line.analyticDistributions.reduce(
                (sum, dist) => sum + dist.percentage,
                0
            );

            // Update error state
            if (Math.abs(totalPercentage - 1) > 0.0001) { // Using small epsilon for float comparison
                setDistributionErrors(prev => ({
                    ...prev,
                    [lineIndex]: `Totaal percentage moet 100% zijn (nu: ${(totalPercentage * 100).toFixed(2)}%)`
                }));
            } else {
                setDistributionErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[lineIndex];
                    return newErrors;
                });
            }

            newLines[lineIndex] = line;
            
            return {
                ...prev,
                lines: newLines
            };
        });
    };

    return (
        <div className="bg-F9F0EE/30 p-4 rounded-lg border border-neutral-primary/10 space-y-4">
            <div className="flex justify-between items-center border-b pb-2 mb-2">
                <div className="text-base font-medium text-neutral-primary">
                    Documentregels
                </div>

                <ButtonNew
                    variant="secondaryMoss"
                    label="Regel toevoegen"
                    onClick={() => {
                        setFormData((prev: any) => {
                            const newLines = [...prev.lines];
                            newLines.push({
                                description: '',
                                totalExclVat: 0,
                                vatPercentage: prev.defaultVatPercentage,
                                vatAmount: 0,
                                totalInclVat: 0,
                                part: prev.parts.length > 0 ? prev.parts[0] : null,
                                analyticDistributions:
                                    building?.units?.length
                                        ? building.units.map((unit) => ({
                                            unit,
                                            percentage: 1 / (building.units?.length || 1),
                                        }))
                                        : [],
                                position: newLines.length + 1,
                            });

                            // Recalculate totals
                            const totals = calculateTotals(newLines);
                            return {
                                ...prev,
                                lines: newLines,
                                totalExclVat: totals.totalExclVat,
                                vatAmount: totals.vatAmount,
                                totalInclVat: totals.totalInclVat,
                            };
                        });
                    }}
                    isDisabled={!formData.projects.length || !formData.parts.length}
                />
            </div>
            
            {formData.lines?.length > 0 ? (
                <div className="space-y-4">
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse table-fixed">
                            <thead>
                                <tr className="bg-neutral-50">
                                    <th className="text-left p-2 border-b text-neutral-primary w-[40%]">Omschrijving</th>
                                    <th className="text-right p-2 border-b text-neutral-primary w-[15%]">Bedrag excl.</th>
                                    <th className="text-right p-2 border-b text-neutral-primary w-[10%]">BTW %</th>
                                    <th className="text-right p-2 border-b text-neutral-primary w-[15%]">Bedrag incl.</th>
                                    <th className="text-center p-2 border-b text-neutral-primary w-[10%]">Acties</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.lines.map((line, index) => (
                                    <React.Fragment key={index}>
                                        <tr className={`border-b ${index % 2 === 0 ? "bg-white" : "bg-F9F0EE/30"}`}>
                                            <td className="p-2">
                                                <Textarea
                                                    value={line.description}
                                                    onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                                                    placeholder="Omschrijving"
                                                    minRows={1}
                                                    classNames={{
                                                        inputWrapper: "bg-white w-full",
                                                        input: "text-sm"
                                                    }}
                                                />
                                            </td>
                                            <td className="p-2">
                                                <Input
                                                    type="number"
                                                    value={line.totalExclVat.toString()}
                                                    onChange={(e) => handleLineChange(index, 'totalExclVat', Number(e.target.value))}
                                                    onFocus={(e: any) => e.target.select()}
                                                    classNames={{
                                                        inputWrapper: "bg-white h-9 min-h-9 justify-end w-full",
                                                        input: "text-right text-sm"
                                                    }}
                                                    startContent={<span className="text-neutral-secondary-mid text-xs">€</span>}
                                                />
                                            </td>
                                            <td className="p-2">
                                                <Select
                                                    selectedKeys={[`${line.vatPercentage ?? 0}`]}
                                                    onChange={(e) => handleLineChange(index, 'vatPercentage', Number(e.target.value))}
                                                    classNames={{
                                                        base: "w-full",
                                                        trigger: "h-9 min-h-9 bg-white text-sm",
                                                        value: "text-right"
                                                    }}
                                                >
                                                    <SelectItem key="21">21%</SelectItem>
                                                    <SelectItem key="6">6%</SelectItem>
                                                    <SelectItem key="0">0%</SelectItem>
                                                </Select>
                                            </td>
                                            <td className="p-2 text-right font-medium">
                                                {formatCurrency(line.totalInclVat ?? 0)}
                                            </td>
                                            <td className="p-2">
                                                <div className="flex justify-center gap-2">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="flat"
                                                        className="bg-moss-primary/10 text-moss-primary min-w-0 w-8 h-8"
                                                        onClick={() => toggleDistributionExpansion(index)}
                                                    >
                                                        <Info size={16} />
                                                    </Button>
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="flat"
                                                        className="bg-brick-primary/10 text-brick-primary min-w-0 w-8 h-8"
                                                        onClick={() => {
                                                            setFormData((prev: any) => {
                                                                const newLines = [...prev.lines];
                                                                newLines.splice(index, 1);
                                                                const totals = calculateTotals(newLines);
                                                                return {
                                                                    ...prev,
                                                                    lines: newLines,
                                                                    totalExclVat: totals.totalExclVat,
                                                                    vatAmount: totals.vatAmount,
                                                                    totalInclVat: totals.totalInclVat
                                                                };
                                                            });
                                                        }}
                                                    >
                                                        <Trash size={16} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                        
                                        {/* Expanded view for project parts and kostenplaatsen */}
                                        {expandedDistributions.includes(index) && (
                                            <tr>
                                                <td colSpan={6} className="p-0">
                                                    <div className="p-3 bg-neutral-50 border-b">
                                                        {/* Project Part Selection with better width */}
                                                        <div className="mb-3">
                                                            <h4 className="text-sm font-medium text-neutral-primary mb-2">
                                                                Project onderdeel
                                                            </h4>
                                                            <Select
                                                                selectedKeys={line.part ? [line.part.id.toString()] : []}
                                                                onChange={(e) => {
                                                                    const selectedPart = formData.projects[0]?.parts.find(
                                                                        p => p.id.toString() === e.target.value
                                                                    );
                                                                    handleLineChange(index, 'part', selectedPart || null);
                                                                }}
                                                                classNames={{
                                                                    base: "w-full max-w-xl",
                                                                    trigger: "h-10 bg-white text-sm"
                                                                }}
                                                            >
                                                                {formData.projects[0]?.parts.map(part => (
                                                                    <SelectItem key={part.id} textValue={part.title}>
                                                                        {part.title}
                                                                    </SelectItem>
                                                                ))}
                                                            </Select>
                                                        </div>

                                                        {/* Kostenplaats verdeling section */}
                                                        <div className="flex justify-between items-center mb-2">
                                                            <h4 className="text-sm font-medium text-neutral-primary">
                                                                Kostenplaats verdeling
                                                            </h4>
                                                            <p className="text-xs text-neutral-secondary-mid">
                                                                Totaal: {formatCurrency(line.totalExclVat)}
                                                            </p>
                                                        </div>
                                                        
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                                            {line.analyticDistributions.map((dist) => (
                                                                <div 
                                                                    key={dist.unit.id}
                                                                    className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white border border-neutral-primary/10"
                                                                >
                                                                    <div className="text-xs text-neutral-secondary-mid ">{dist.unit.id}</div>
                                                                    <div className="min-w-0 flex-1">
                                                                        <p className="text-xs text-neutral-secondary-mid truncate">
                                                                            {formatCurrency(line.totalExclVat * dist.percentage)}
                                                                        </p>
                                                                    </div>
                                                                    <Input
                                                                        type="number"
                                                                        value={(dist.percentage * 100).toFixed(2)}
                                                                        onChange={(e) => handleAnalyticDistributionChange(
                                                                            index,
                                                                            dist.unit.id as string,
                                                                            Number(e.target.value) / 100
                                                                        )}
                                                                        size="sm"
                                                                        endContent="%"
                                                                        isInvalid={!!distributionErrors[index]}
                                                                        classNames={{
                                                                            inputWrapper: "h-8 min-h-8 bg-white w-28 shrink-0",
                                                                            input: "text-right text-sm pr-2"
                                                                        }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                        {distributionErrors[index] && (
                                                            <p className="text-brick-primary text-sm mt-2 bg-brick-secondary-fine p-1 rounded-sm">
                                                                {distributionErrors[index]}
                                                            </p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Totals display */}
                    <div className="flex justify-end mt-4 space-x-6 bg-white p-4 rounded-lg border border-neutral-primary/10">
                        <div>
                            <p className="text-sm text-neutral-secondary-mid">Totaal excl. BTW</p>
                            <p className="font-medium">{formatCurrency(formData.totalExclVat)}</p>
                        </div>
                        <div>
                            <p className="text-sm text-neutral-secondary-mid">BTW</p>
                            <p className="font-medium">{formatCurrency(formData.vatAmount)}</p>
                        </div>
                        <div>
                            <p className="text-sm text-neutral-secondary-mid">Totaal incl. BTW</p>
                            <p className="font-medium">{formatCurrency(formData.totalInclVat)}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-dashed border-neutral-primary/30 p-6 text-center">
                    <p className="text-neutral-secondary-mid">Geen factuurregels toegevoegd.</p>
                    <p className="text-sm">
                        Upload een document en gebruik Ella voor automatische analyse of voeg handmatig een regel toe.
                    </p>
                </div>
            )}
        </div>
    );
};

export default DocumentLinesSection;